import React, {PureComponent, ReactElement, useEffect, useState} from "react";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "@/shared/BaseFragment";
import {BaseApp, DIALOG_FLAG_DISABLE_BACKDROP_CLICK} from "@/shared/BaseApp";
import {
  StyledBoxColumn,
  StyledBoxRow,
  StyledCircularProgress,
  StyledEmpty,
  StyledSpan
} from "@/shared/StyledComponents";
import {
  BORDER_RADIUS_MEDIUM,
  PAGE_FRAGMENT_HALF_WIDTH,
  PAGE_FRAGMENT_SM_WIDTH,
  PAGE_FRAGMENT_WIDTH,
  PD_MD,
  PD_XLG,
  PD_XLG2,
  SZ_LG
} from "@/shared/dimens";
import {Button, Typography} from "@mui/material";
import {PasskeyConfig} from "@/shared/passkey";
import {PasskeyInput} from "@/shared/PasskeyInput";
import {PathProps} from "@/shared";
import {getAuth, signOut} from "@firebase/auth";
import {EmptyConfig} from "@/shared/types";
import {ArrowBackIosNewOutlined, HelpOutlineOutlined, StoreOutlined, WavingHandOutlined} from "@mui/icons-material";
import {CheckinButton} from "./Components";
import {AccountProfileContextTrait, Page} from "@/libwyze-shared/types";
import {Clock, ClockGranularity, ClockListener} from "../shared/clock";
import dayjs from "dayjs";
import {sleep} from "../shared/sleep";

class ClockView extends PureComponent implements ClockListener {

  componentDidMount() {
    Clock.getInstance().addListener(ClockGranularity.MINUTE, this);
  }

  componentWillUnmount() {
    Clock.getInstance().removeListener(ClockGranularity.MINUTE, this);
  }

  tick(value: number) {
    this.forceUpdate();
  }

  render(): React.JSX.Element {
    const now = dayjs();
    const text = `${now.format("DD MMM, hh:mm")} ${now.format("A")}`;
    return <Typography variant="h6" style={{flexShrink: 0}}>{text}</Typography>
  }
}

function UnlockFragment(props: { path: PathProps, onUnlockWithPin(pin: string): void }): ReactElement {
  const config: PasskeyConfig = {length: 6, type: "numeric"};
  const [passkey, setPasskey] = useState<string>("");
  return <StyledBoxColumn style={{width: PAGE_FRAGMENT_SM_WIDTH, padding: PD_MD}}>
    <Typography style={{textAlign: "center"}}>Enter PIN to unlock</Typography>
    <StyledBoxColumn style={{alignItems: "center"}}>
      <PasskeyInput
        config={config}
        onPasskeyInputChanged={passkey => setPasskey(passkey)}
      />
    </StyledBoxColumn>
    <Button
      onClick={() => props.onUnlockWithPin(passkey)}
      variant={"contained"} disabled={passkey.length !== config.length}>Unlock</Button>
  </StyledBoxColumn>
}

function CallHelpFragment(props: { onClose: () => void }): ReactElement {
  const [notified, setNotified] = useState<boolean>();
  useEffect(() => {
    (async function () {
      await sleep(1000);
      // TODO Notify frontdesk
      setNotified(true);
    })();
  }, []);
  return <StyledBoxColumn
    style={{width: PAGE_FRAGMENT_WIDTH, padding: PD_XLG2, alignItems: "center"}}>
    {notified !== undefined
      ? (notified
        ? <>
          <WavingHandOutlined style={{fontSize: "400%"}}/>
          <Typography variant="h3" style={{textAlign: "center"}}>Help is on the way.</Typography>
          <Typography style={{fontSize: "150%", textAlign: "center"}}>A member of our reception team has been
            notified. Please wait a moment, someone will be with you shortly.</Typography>
          <CheckinButton style={{marginTop: PD_XLG, width: PAGE_FRAGMENT_HALF_WIDTH}}
                         onClick={props.onClose} variant={"outlined"}>Close</CheckinButton>
        </>
        : <>
          <StyledEmpty emptyConfig={{
            title: "Something went wrong",
            text: "Please approach the reception for help.",
          }}/>
          <CheckinButton style={{marginTop: PD_XLG, width: PAGE_FRAGMENT_HALF_WIDTH}}
                         onClick={props.onClose} variant={"outlined"}>Close</CheckinButton>
        </>)
      : <StyledCircularProgress/>}
  </StyledBoxColumn>;
}

export type BaseCheckinPlayerFragmentProps = BaseFragmentProps & {
  noChrome?: boolean,
  practicePage: Page,
}

export type BaseCheckinPlayerFragmentState<ST> = BaseFragmentState & {
  screenStack: ST[],
  transitioning?: boolean,
  pendingScreenStack?: ST[],
}

export abstract class BaseCheckinPlayerFragment<ST, P extends BaseCheckinPlayerFragmentProps = BaseCheckinPlayerFragmentProps, S extends BaseCheckinPlayerFragmentState<ST> = BaseCheckinPlayerFragmentState<ST>> extends BaseFragment<P, S> {

  protected onBackOverride: (() => boolean) | null = null;

  protected getEmptyConfig(): EmptyConfig {
    return {
      title: "No practice found",
      text: "Ask your admin to add one or more practice locations and reload this page.",
      iconType: StoreOutlined,
    }
  }

  protected renderContainerContent(): React.ReactElement | null {
    const practiceId = BaseApp.CONTEXT.getTrait<AccountProfileContextTrait>().getPracticePageId();
    if (!practiceId) {
      return this.renderEmpty();
    }
    return !this.props.noChrome
      ? <StyledBoxColumn
        style={{userSelect: "none", width: "100vw", height: "100vh", justifyContent: "center", alignItems: "center"}}>
        <StyledBoxColumn style={{
          // background: darken(COLOR_PRIMARY, 0.25),
          // color: "white",
          position: "relative",
          width: 1280,
          height: 720,
          alignItems: "center",
          border: "24px solid black",
          fontSize: 24,
          borderRadius: BORDER_RADIUS_MEDIUM,
        }}>
          {this.renderContent()}
        </StyledBoxColumn>
      </StyledBoxColumn>
      : this.renderContent();
  }

  private renderContent(): React.ReactElement {
    return <>
      <StyledBoxRow
        style={{
          position: "relative",
          zIndex: 100,
          width: "100%",
          height: SZ_LG,
          flexShrink: 0,
          padding: `0px ${PD_XLG2}px`,
          alignItems: "center",
        }}>
        {this.state.screenStack.length > 1
          ? <CheckinButton
            startIcon={<ArrowBackIosNewOutlined/>}
            onClick={() => this.handleBack()}>
            Back
          </CheckinButton>
          : null}
        <StyledSpan/>
        <CheckinButton
          onClick={() => {
            const id = BaseApp.CONTEXT.showDialog({flags: DIALOG_FLAG_DISABLE_BACKDROP_CLICK}, () =>
              <CallHelpFragment onClose={() => BaseApp.CONTEXT.hideDialog(id)}/>);
          }}
          startIcon={<HelpOutlineOutlined/>}>
          Call Help
        </CheckinButton>
        <div style={{
          pointerEvents: "none",
          position: "absolute",
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <Typography style={{textTransform: "uppercase"}}>
            <ClockView/>
          </Typography>
        </div>
      </StyledBoxRow>
      <StyledBoxColumn
        style={{
          flexGrow: 1,
          width: "100%",
          padding: `0px ${PD_XLG2}px ${PD_XLG2}px ${PD_XLG2}px`,
          transition: `opacity ${BaseCheckinPlayerFragment.TRANSITION_DURATION}ms ease, transform ${BaseCheckinPlayerFragment.TRANSITION_DURATION}ms ease`,
          opacity: this.state.transitioning ? 0 : 1,
          transform: this.state.transitioning ? "scale(0.96)" : "scale(1)",
        }}>
        {this.renderScreen()}
      </StyledBoxColumn>
      <StyledBoxRow style={{position: "absolute", right: 24, bottom: 8, alignItems: "center", gap: 4, opacity: 0.8}}>
        <Typography variant={"caption"}>Powered by</Typography>
        <img src={"/images/dentwyze_logotype.svg"} style={{height: 20}}/>
      </StyledBoxRow>
    </>;
  }

  protected currentScreenType(): ST {
    return this.state.screenStack[this.state.screenStack.length - 1];
  }

  private handleBack(): void {
    if (this.onBackOverride && this.onBackOverride()) return;
    this.popScreenType();
  }

  private static readonly TRANSITION_DURATION = 200;

  private transitionTo(nextStack: ST[]): void {
    if (this.state.transitioning) {
      return;
    }
    this.setState({transitioning: true, pendingScreenStack: nextStack});
    setTimeout(() => {
      this.setState({
        screenStack: this.state.pendingScreenStack,
        transitioning: false,
        pendingScreenStack: undefined,
      });
    }, BaseCheckinPlayerFragment.TRANSITION_DURATION);
  }

  protected setScreenType(screenType: ST): void {
    this.transitionTo([screenType]);
  }

  protected pushScreenType(screenType: ST): void {
    this.transitionTo([...this.state.screenStack, screenType]);
  }

  protected popScreenType(): ST {
    const stack = [...this.state.screenStack];
    const popped = stack.pop();
    this.transitionTo(stack);
    return popped;
  }

  private showUnlockDialog() {
    BaseApp.CONTEXT.showDialog(null, props => <UnlockFragment path={this.props.path} onUnlockWithPin={() => {
      signOut(getAuth())
        .then(() => window.location.href = "/");
    }}/>);
  }

  protected abstract renderScreen(): ReactElement;
}